import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import {
  ComponentMultiStyleConfig,
  Flex,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { ReactElement, useCallback, useMemo } from "react";
import {
  ChangeBadgeProperties,
  SearchDropdownDisplayValue,
} from "../../../../lib";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  CMButtonFormCommentProps,
  CMFormControlHeader,
  FormRequiredLabelProps,
} from "../../index";
import { CMFormInputBaseSearchAndSelect } from "../../../control";
import { hasValue } from "../../../../lib/util/common-util";

export type CMFormInputSearchAndSelectProps<T extends string | number> = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";

  /**
   * trueの場合、ヘッダなしとなる。
   * この場合、changeBadgePropertiesやcommentButtonPropsが存在しても、変更やコメントを扱わない。
   */
  noHeader?: boolean;

  label?: string;
  placeholder?: string;
  placeholderPopover?: string;
  /**
   * 入力可能な最大文字数。
   */
  maxLength?: number;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  value?: T | null;

  hideClearButton?: boolean;

  labelSubmit?: string;
  labelClear?: string;

  selectButtonRightIcon?: ReactElement;
  popoverTitle?: string;

  searchResultNone?: string;

  /**
   * 値を表示値に変換する関数。
   * heading: 各行の手前に、薄い字で表示する部分。必須ではない。
   * main: 表示値。必須。
   */
  toDisplayValue: (value: T) => Promise<SearchDropdownDisplayValue>;
  /**
   * ヘッダの変更前の値の表示値。
   * 省略した場合、変更前の値をtoDisplayValueで変換し、その結果のmainを用いる。
   */
  changedFromDisplayValue?: string;

  /**
   * 検索を行う関数。
   * @param text 検索条件となるテキスト。
   * @return 検索結果。
   */
  onSearch?: (text: string) => Promise<T[]>;
  onChange?: (value: T | null) => void;

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   */
  changeBadgeProperties?: ChangeBadgeProperties<T>;
  /**
   * 2つの値が同じかどうかを判定する関数。
   * 差分モード時に使用する。
   * 省略した場合は、単純な === で比較する。
   */
  checkEqualityFn?: (v0: T, v1: T) => boolean | Promise<boolean>;

  /**
   * コメントボタンのprops。
   * 定義されている場合、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  /**
   * ポップオーバーを出す時、portalを用いるならtrue。
   * デフォルト値はfalse。
   */
  usePortal?: boolean;
} & ComponentStyleProps;

/**
 * Inputエリアに入力された文字列で検索処理を呼び出し、検索結果をメニュー表示する。
 * メニュー選択後に各ボタンを押下することで、呼び出し元に値の変更を通知する。
 *
 * 確定ボタン押下時   : メニュー選択済み項目の情報
 * クリアボタン押下時 : null
 */
export const CMFormInputSearchAndSelect = <T extends string | number>({
  editMode,
  size,

  noHeader = false,

  label,
  placeholder,
  placeholderPopover,
  maxLength = 100,
  menuShowMaxCount,

  value = null,

  hideClearButton = false,

  labelSubmit,
  labelClear,

  selectButtonRightIcon = <ChevronDownIcon />,
  popoverTitle,

  searchResultNone,

  toDisplayValue,
  changedFromDisplayValue,

  onSearch,
  onChange,

  requiredLabelProps,

  changeBadgeProperties,
  checkEqualityFn,

  commentButtonProps,

  usePortal = false,

  sx,
  ...rest
}: CMFormInputSearchAndSelectProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputSearchAndSelectStyles", {});

  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );

  const toDisplayValueForHeader = useCallback(
    async (v: T) => {
      if (hasValue(changedFromDisplayValue)) {
        return changedFromDisplayValue;
      }
      const result = await toDisplayValue(v);
      return result.mainLabel;
    },
    [changedFromDisplayValue, toDisplayValue],
  );

  const containerStyles = useMemo(
    () => ({ ...styles.container, ...sx }),
    [styles, sx],
  );

  return (
    <Flex sx={containerStyles} {...rest}>
      {!noHeader && (
        <CMFormControlHeader<T>
          label={label}
          value={value}
          requiredLabelProps={requiredLabelProps}
          changeBadgeProperties={changeBadgeProperties}
          toDisplayValue={toDisplayValueForHeader}
          checkEqualityFn={checkEqualityFn}
          commentButtonProps={commentButtonProps}
          sx={styles.header}
        />
      )}
      <CMFormInputBaseSearchAndSelect
        editMode={editMode}
        variant={isDiffMode ? "diff" : "default"}
        size={size}
        placeholder={placeholder}
        placeholderPopover={placeholderPopover}
        maxLength={maxLength}
        menuShowMaxCount={menuShowMaxCount}
        value={value}
        hideClearButton={hideClearButton}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        selectButtonRightIcon={selectButtonRightIcon}
        popoverTitle={popoverTitle}
        searchResultNone={searchResultNone}
        toDisplayValue={toDisplayValue}
        changedFrom={changeBadgeProperties?.changedFrom}
        checkEqualityFn={checkEqualityFn}
        onSearch={onSearch}
        onChange={onChange}
        usePortal={usePortal}
        sx={styles.main}
      />
    </Flex>
  );
};

export const CMFormInputSearchAndSelectStyles: ComponentMultiStyleConfig = {
  parts: ["container", "header", "main"],
  baseStyle: {
    container: {
      flexFlow: "column nowrap",
      alignItems: "flex-start",
    },
    header: {
      mb: "2px",
    },
    main: {
      flex: 1,
      alignSelf: "stretch",
    },
  },
};
